import { useState } from 'react';
import { AttendanceImportComponent } from './attendance-import-component';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { AttendanceParsingError, CitizenAttendanceDto } from '../../../../api-service/clients';

function formatParseErrors(errors: AttendanceParsingError[]): string {
  return errors
    .map(e => {
      const prefix = e.row > 0 ? `Række ${e.row}: ` : '';
      return `${prefix}${e.errorMessage}`;
    })
    .join('\n');
}

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
}

const attendanceClient = moduleClientFactory.createAttendanceClient();

export function AttendanceImport() {
  const [citizens, setCitizens] = useState<CitizenAttendanceDto[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function handleFileSelected(file: File) {
    setParseError(null);
    setSubmitError(null);
    setIsSubmitting(true);
    setCitizens([]);

    try {
      const base64Content = await fileToBase64(file);
      const response = await attendanceClient.attendanceCsvParser({ content: base64Content });

      const { citizens: importedCitizens, errors } = response;
      const hasErrors = errors && errors.length > 0;

      if (importedCitizens?.length === 0 && !hasErrors) {
        setParseError('Ingen borgerdata fundet i filen. Kontrollér at filen har korrekt CSV-format.');
        return;
      }

      if (hasErrors) {
        setParseError('Fejl i filen. Ret fejlene og upload filen igen.\n\n' + formatParseErrors(errors));
      }

      setCitizens(importedCitizens || []);
    } catch (error: any) {
      const serverMessage = error?.response?.data?.detail || error?.response?.data?.title;
      setParseError(serverMessage || 'Kunne ikke læse filen. Kontrollér at det er en gyldig CSV-fil.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleApprove() {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const response = await attendanceClient.attendanceImport({ citizens });
      const { validationErrors } = response;

      if (validationErrors && validationErrors.length > 0) {
        const errorMessages = validationErrors.map(e => `• ${e.errorMessage}`).join('\n');
        setSubmitError('Validering fejlede:\n\n' + errorMessages);
        return;
      }

      window.close();
    } catch (error: any) {
      const serverMessage = error?.response?.data?.detail || error?.response?.data?.title;
      setSubmitError(serverMessage || 'Der opstod en fejl ved godkendelse af protokollen.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AttendanceImportComponent
      citizens={citizens}
      parseError={parseError}
      submitError={submitError}
      isSubmitting={isSubmitting}
      onFileSelected={handleFileSelected}
      onApprove={handleApprove}
    />
  );
}
